import type { ISnippetRule, SnippetType } from '@carboncopyco/types';
import { useCallback, useMemo, useRef, useState } from 'react';
import {
  ManagerMessageBox,
  RulesContent,
  RulesManager,
  RulesManagerEmptySearch,
  RulesManagerFilters,
  type RulesManagerProps,
  useRulesManagerFilters,
} from '../RulesManager';
import { AddCustomCodeButton } from './AddCustomCodeButton';
import { CustomCodeContent } from './CustomCodeContent/CustomCodeContent';
import { CustomCodeCreationForm } from './CustomCodeContent/CustomCodeCreationForm';

export type Filters = 'all' | 'js' | 'css';

export interface CustomCodeManagerProps
  extends Pick<RulesManagerProps, 'isOpen' | 'onClose' | 'onLearnMoreClick'> {
  snippets: ISnippetRule[];
  createSnippet: (snippet: ISnippetRule) => Promise<unknown>;
  updateSnippet: (snippet: ISnippetRule) => Promise<unknown>;
  deleteSnippet: (snippet: ISnippetRule) => Promise<unknown>;
  reorderSnippets: (
    moved: ISnippetRule,
    after: ISnippetRule
  ) => Promise<unknown>;
}

const linkUrl = 'https://help.demostack.com/en/articles/7862142';

export const CustomCodeManager = ({
  isOpen,
  onClose,
  snippets,
  onLearnMoreClick,
  updateSnippet,
  deleteSnippet,
  createSnippet,
  reorderSnippets,
}: CustomCodeManagerProps) => {
  const [snippetToCreate, setSnippetToCreate] = useState<SnippetType>(null);
  const contentRef = useRef<HTMLDivElement>();

  const showEmptyState = !snippets.length && !snippetToCreate;

  const filterBySnippetType = useCallback(
    (snippet: ISnippetRule, filter: Filters) => {
      return filter === 'all' || snippet.snippetType === filter;
    },
    []
  );
  const filterOptions = useMemo(
    () => ({
      initialFilter: 'all' as Filters,
      searchKeys: ['name'],
      dataSet: snippets,
      filterFunc: filterBySnippetType,
    }),
    [snippets, filterBySnippetType]
  );

  const {
    setSearchValue,
    searchValue,
    filter,
    setFilter,
    filteredList: filteredSnippets,
    isFiltersActive,
  } = useRulesManagerFilters<Filters, ISnippetRule>(filterOptions);

  return (
    <RulesManager
      isOpen={isOpen}
      onClose={snippetToCreate === null ? onClose : undefined}
      onLearnMoreClick={onLearnMoreClick}
    >
      <RulesManager.Header>
        <RulesManager.Header.Title label="Custom Code" />
        <RulesManager.Header.Action>
          <AddCustomCodeButton
            snippets={snippets}
            onSelect={(type) => {
              setSnippetToCreate(type);
              contentRef.current?.scrollTo({ top: 0 });
            }}
          />
        </RulesManager.Header.Action>
      </RulesManager.Header>
      {!showEmptyState && <ManagerMessageBox linkUrl={linkUrl} />}
      {showEmptyState ? (
        <RulesManager.EmptyState color="green">
          <RulesManager.EmptyState.Title label="Gain full control with custom code" />
          <RulesManager.EmptyState.Subtitle
            label="Not sure how it works?"
            linkLabel="Learn more"
            linkUrl={linkUrl}
          />
        </RulesManager.EmptyState>
      ) : (
        <RulesManager.Content ref={contentRef}>
          <RulesContent>
            {!!snippets.length && (
              <RulesManagerFilters<Filters>
                onSearchChange={setSearchValue}
                onFiltersChange={setFilter}
                dropdownItems={[
                  { id: 'all', label: 'Type' },
                  { id: 'js', label: 'JavaScript' },
                  { id: 'css', label: 'CSS' },
                ]}
                searchValue={searchValue}
                selectedFilter={filter}
              />
            )}
            <RulesContent.CreationForm show={!!snippetToCreate}>
              <CustomCodeCreationForm
                type={snippetToCreate}
                autoFocus
                onSubmit={async (snippet) => {
                  await createSnippet(snippet);
                  setSnippetToCreate(null);
                }}
                snippets={snippets}
                onCancel={() => setSnippetToCreate(null)}
              />
            </RulesContent.CreationForm>
            {filteredSnippets.length || snippetToCreate ? (
              <CustomCodeContent
                isFiltersActive={isFiltersActive}
                snippets={filteredSnippets}
                createSnippet={createSnippet}
                updateSnippet={updateSnippet}
                deleteSnippet={deleteSnippet}
                reorderSnippets={reorderSnippets}
              />
            ) : (
              <RulesManagerEmptySearch ruleType="snippets" />
            )}
          </RulesContent>
        </RulesManager.Content>
      )}
    </RulesManager>
  );
};
